import React from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import DatePicker from 'react-datepicker';
import { datePickerLocale } from '../v2/components/common/datepicker-locale';
import '../v2/pages/timesheet/timeSheetsPage/timeSheetsPage.scss';

/* Portal the react-datepicker popper out of the DOM tree so it can't be
   clipped by any ancestor with `overflow: hidden` (rounded card edges)
   or `transform` (entrance animations promote the element to a containing
   block for fixed descendants). Rendering directly under <body> means
   neither constraint applies — the calendar drops into the viewport at
   its computed position regardless of where this component is mounted. */
const PopperPortal: React.FC<{ children: React.ReactNode }> = ({ children }) =>
  ReactDOM.createPortal(children, document.body);

/* Bridges between the parent's ISO-string contract (`yyyy-mm-dd`) and
   react-datepicker's `Date | null` model so the component's onChange
   callback signature stays identical to the old <input type="date">. */
const parseIso = (s?: string): Date | null => {
  if (!s) return null;
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
};
const toIso = (d: Date | null): string => {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

interface TimesheetFilterHeaderProps {
  fromDate: string;
  toDate: string;
  onFromDateChange: (date: string) => void;
  onToDateChange: (date: string) => void;
  onLoad: () => void;
  onSave: () => void;
  placeholder?: string;
  hasNewEntries?: boolean;
}

const TimesheetFilterHeader: React.FC<TimesheetFilterHeaderProps> = ({
  fromDate,
  toDate,
  onFromDateChange,
  onToDateChange,
  placeholder = 'yyyy/mm/dd',
  onLoad,
  onSave,
  hasNewEntries = false,
}) => {
  const { t, i18n } = useTranslation();

  const isLoadDisabled = (!fromDate && !!toDate) || (!!fromDate && !toDate);

  return (
    <div className="timesheet-filter-header">
      <div className="filter-field">
        <label className="filter-label" style={{ color: "var(--text-icons-light-base-main, #252525)", fontFamily: "Plus Jakarta Sans", fontSize: 14, fontStyle: "normal", fontWeight: 500, lineHeight: "20px", marginBottom: "0px !important" }}>
          {t("FROMS")}
        </label>
        <div className="filter-input-wrapper">
          {/* Replaced native <input type="date"> with react-datepicker so
              the displayed format is `yyyy/mm/dd` in every browser. The
              parent contract (`onFromDateChange` receives `yyyy-mm-dd`)
              is unchanged via the parseIso / toIso helpers above. */}
          <DatePicker
            selected={parseIso(fromDate)}
            onChange={(d: Date | null) => onFromDateChange(toIso(d))}
            dateFormat="yyyy/MM/dd"
            placeholderText={placeholder}
            locale={datePickerLocale(i18n.language)}
            className="filter-input"
            wrapperClassName="tfh-dp"
            /* Portal + fixed strategy together = the popper is rendered
               as `position: fixed` directly under <body>, guaranteed to
               escape any ancestor's overflow:hidden or transform-based
               containing block (timesheet toolbar, page entrance fade). */
            popperContainer={PopperPortal}
            popperProps={{ strategy: 'fixed' }}
            popperClassName="tfh-dp-popper"
            calendarClassName="tfh-dp-calendar"
            /* Block typing while keeping click-to-open. Don't use the
               readOnly prop — in react-datepicker v4 it disables the
               calendar trigger entirely. */
            onChangeRaw={(e: React.SyntheticEvent | undefined) => e?.preventDefault()}
            showMonthDropdown
            showYearDropdown
            dropdownMode="scroll"
            autoComplete="off"
          />
        </div>
      </div>

      <div className="filter-field">
        <label className="filter-label" style={{ color: "var(--text-icons-light-base-main, #252525)", fontFamily: "Plus Jakarta Sans", fontSize: 14, fontStyle: "normal", fontWeight: 500, lineHeight: "20px", marginBottom: "0px !important" }}>
          {t("TO")}
        </label>
        <div className="filter-input-wrapper">
          {/* Same picker as the From input. `minDate` keeps the existing
              behaviour: To can't precede From. */}
          <DatePicker
            selected={parseIso(toDate)}
            onChange={(d: Date | null) => onToDateChange(toIso(d))}
            dateFormat="yyyy/MM/dd"
            placeholderText={placeholder}
            locale={datePickerLocale(i18n.language)}
            minDate={parseIso(fromDate) ?? undefined}
            className="filter-input"
            wrapperClassName="tfh-dp"
            popperContainer={PopperPortal}
            popperProps={{ strategy: 'fixed' }}
            popperClassName="tfh-dp-popper"
            calendarClassName="tfh-dp-calendar"
            onChangeRaw={(e: React.SyntheticEvent | undefined) => e?.preventDefault()}
            showMonthDropdown
            showYearDropdown
            dropdownMode="scroll"
            autoComplete="off"
          />
        </div>
      </div>

      <button
        className={`load-btn ${isLoadDisabled ? "disabled" : ""}`}
        onClick={onLoad}
        disabled={isLoadDisabled}
        style={{
          opacity: isLoadDisabled ? 0.5 : 1,
          cursor: isLoadDisabled ? "not-allowed" : "pointer",
        }}
      >
        {t("LOAD")}
      </button>

      <button className="save-timesheet-btn" data-tour="save-timesheet" onClick={onSave} disabled={!hasNewEntries}>
        {t("SAVE_TIMESHEET")}
      </button>
    </div>
  );
};

export default TimesheetFilterHeader;
